/**
 * Where an `as-if` credential may go (F-0709, `docs/concept-start-a-work-area-as-if.md` ASIF-29).
 *
 * **Default deny.** An as-if credential is the real person's own sign-in narrowed to one work area. Every
 * route and resolver that does not check access through the narrowed seam (`AccessControlService`) would
 * answer it as the person, outside the work area and with the person's own rights, so the credential is
 * refused everywhere (`403`, {@link AS_IF_NOT_HERE}) except on the routes listed here. The refusal is
 * made in one place, the three authentication guards (`assertAsIfMayEnter`), not per route.
 *
 * Each entry is a route bound to the view's own work area: `workAreaParam` names the path parameter
 * that carries a work area's id, and a request whose value differs from the work area the view is bound
 * to is refused as well. ⛔ Adding an entry is one reviewed line, and its route must go through the
 * narrowed access check (`as-if-routes.guard.spec.ts` reads the controllers and fails on an entry that
 * names no route, or a parameter the route does not have).
 */

/**
 * I-2199 (W1): the close code of a graphql-ws handshake refused for the credential's KIND (an as-if
 * credential holds no socket, ASIF-29). Final for that credential. A handshake refused for anything else
 * (an expired token after sleep) is the library's own 4403, which the client retries with a refreshed
 * token; the two must never share a code.
 */
export const SOCKET_CREDENTIAL_KIND_REFUSED_CLOSE_CODE = 4430;

/** The reason an as-if credential is refused at a route that is not on the list. */
export const AS_IF_NOT_HERE = 'as-if-not-here';

/**
 * The 401 a view's token gets once the view has ended (Stop, a demotion, the setting turned off, the
 * sign-in it came from ending, the cap or an idle expiry). Distinct from the generic `credential-revoked`
 * and `credential-expired` codes so the web can drop the banner and go on as the person, never sign them
 * out. The body's `reason` is why: the row's revoked reason, `expired`, `origin-ended` or `setting-off`.
 */
export const AS_IF_ENDED = 'as-if-ended';
export const AS_IF_ENDED_MESSAGE = 'This view has ended. Start it again, or go on as yourself.';

export interface IAsIfAllowedRoute {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** The route as Nest registers it, without the global prefix or a leading slash. */
  path: string;
  /**
   * The path parameter that carries a work area's id. Exactly one of this, `workItemParam` and
   * `workAreaQuery`.
   */
  workAreaParam?: string;
  /**
   * I-2557 (ASIF-38): the query parameter that carries a work area's id, for a read that names its work
   * area in the query rather than the path. Judged as a path parameter is: absent, or another work
   * area's id, and the request is refused.
   */
  workAreaQuery?: string;
  /**
   * The path parameter that carries a work item's id: the gate resolves the item's own work area and
   * compares that one, so an item of a sibling work area is refused however it is named.
   */
  workItemParam?: string;
  /**
   * The route answers a view less than it answers the person (I-2167): the response is filtered by the
   * service that serves it, which asks whether the caller holds a view. `shared-views-only` is the
   * work area's saved views: the shared ones, never the holder's private views, and nothing saved on a
   * read. A route that carries a filter is held by `as-if-routes.guard.spec.ts` to a service that applies it.
   */
  viewFilter?: 'shared-views-only';
}

export const AS_IF_ALLOWED_ROUTES: readonly IAsIfAllowedRoute[] = [
  // Stop: ends the view (or the views a session started) in the work area it is bound to.
  { method: 'DELETE', path: 'work-areas/:workAreaId/as-if', workAreaParam: 'workAreaId' },
  // The work area itself, answered through `WorkAreaAccess` as the target role.
  { method: 'GET', path: 'work-areas/:id', workAreaParam: 'id' },
  // What a work area's pages read (request0710s, 2026-10-26): dashboard pages and their documents, the
  // documents and resources lists, the estimate count, the attachments stream. Reads only, each asked
  // of `WorkAreaAccess` for the work area in its path.
  { method: 'GET', path: 'work-areas/:id/dashboard-pages', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/dashboard-pages/checkpoint', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/dashboard-pages/:pageId/documents', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/documents', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/documents/checkpoint', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/documents/:documentId', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/resources', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/resources/checkpoint', workAreaParam: 'id' },
  { method: 'GET', path: 'work-areas/:id/estimated-item-count', workAreaParam: 'id' },
  // I-2484: the package's words in the reader's language, for the names its pages and items carry.
  // Answered as the view's role (`ConfigurationPackagesService.labels`: a guest role reads the words
  // of its pages, its shared items and its own name; `package-labels.as-if.realdb.spec.ts`).
  { method: 'GET', path: 'work-areas/:projectId/packages/labels', workAreaParam: 'projectId' },
  // I-2496 (AGM-77): the package's word for each role, which every role choice and list reads, by
  // the same rule as the labels (`ConfigurationPackagesService.roleWords`).
  { method: 'GET', path: 'work-areas/:projectId/packages/role-words', workAreaParam: 'projectId' },
  // I-2167: the sidebar's saved views, read on every screen at 1280. Only the shared ones: the holder's
  // private views are theirs, not the role's, and a read of them repairs and saves.
  {
    method: 'GET',
    path: 'work-areas/:projectId/views',
    workAreaParam: 'projectId',
    viewFilter: 'shared-views-only',
  },
  { method: 'GET', path: 'work-areas/:projectId/attachments', workAreaParam: 'projectId' },
  // F-0710 CRQ-2: what one upload may hold here, read by whoever may read the work area's items.
  { method: 'GET', path: 'work-areas/:projectId/attachments/limits', workAreaParam: 'projectId' },
  // Filing a request as a guest role (ASIF-3): the form, the filing and the files added to the filed item.
  // The roles come from `guestRolesOf`, which answers the view's own, and no reporter is counted.
  {
    method: 'GET',
    path: 'shared-items/:workAreaId/filing-forms/:itemTypeKey',
    workAreaParam: 'workAreaId',
  },
  { method: 'POST', path: 'shared-items/:workAreaId', workAreaParam: 'workAreaId' },
  {
    method: 'POST',
    path: 'shared-items/:workAreaId/:key/attachments',
    workAreaParam: 'workAreaId',
  },
  // The comment a view writes as the role (ASIF-3): the item's own work area is resolved and compared.
  { method: 'POST', path: 'work-items/:workItemId/comments', workItemParam: 'workItemId' },
  { method: 'POST', path: 'feedback/:feedbackId/comments', workItemParam: 'feedbackId' },
  // A work item's documents: the item's own work area is resolved and compared (`workItemParam`).
  { method: 'GET', path: 'work-items/:workItemId/documents', workItemParam: 'workItemId' },
  // I-2557 (ASIF-38): what the item page reads beside the item, so a view opens an item as its role
  // reads it. Each asks `WorkAreaAccess` for `work_item.read` on the item's own work area (the
  // deliveries list asks `readableItemIds`, a member's or a guest role's reach), so the role decides.
  { method: 'GET', path: 'work-items/:workItemId/comments', workItemParam: 'workItemId' },
  { method: 'GET', path: 'work-items/:workItemId/attachments', workItemParam: 'workItemId' },
  { method: 'GET', path: 'work-items/:workItemId/deliveries', workItemParam: 'workItemId' },
  { method: 'GET', path: 'work-items/:workItemId/deliveries/preview', workItemParam: 'workItemId' },
  {
    method: 'GET',
    path: 'work-areas/:projectId/work-items/:workItemId/external-links',
    workAreaParam: 'projectId',
  },
  {
    method: 'GET',
    path: 'work-areas/:projectId/work-items/:workItemId/external-actions',
    workAreaParam: 'projectId',
  },
  // I-2557 (ASIF-38): the request form's read, as the role files one (`RequestAccessService.answer`
  // asks `work_item.read` of the work area).
  {
    method: 'GET',
    path: 'work-areas/:workAreaId/item-types/:itemTypeKey/request-access',
    workAreaParam: 'workAreaId',
  },
  // I-2557 (ASIF-38): the Home's package checklist, a manager's read (`work_area.manage`), so a view as
  // an Admin sees it and a Viewer's view is refused it as the Viewer is.
  { method: 'GET', path: 'work-areas/:projectId/packages/checklist', workAreaParam: 'projectId' },
];

/**
 * The first path segment of every controller that serves a person's own state: their account and
 * credentials, preferences, notifications, devices and the people list (F-0709 D3, ASIF-13). Under a
 * named-member target these would resolve to the member, whose personal state an admin is not entitled
 * to read, so none of them may ever be on {@link AS_IF_ALLOWED_ROUTES}: a register test fails an entry
 * that starts with one, and a route here is refused whatever the view is as.
 */
export const AS_IF_PERSONAL_ROUTE_PREFIXES: readonly string[] = [
  'auth',
  'users',
  'me',
  'notifications',
  'user-preferences',
  'user-view-states',
  'push',
];

/**
 * Personal routes named by their whole path, because their first segment also serves a listed route:
 * `GET shared-items` is the person's sharing inbox (what has been shared with the holder, in every work
 * area), while `shared-items/:workAreaId/...` is a view's own filing as a guest role (ASIF-3). Likewise
 * `GET invitations/mine` (I-2249): the invitations addressed to the holder's own account, read by the
 * profile line's badge on every screen (I-2233); a view is a role in one work area, not the person, and
 * has no invitations of its own, so the tab does not ask and the server would refuse it. The other
 * `invitations/...` routes (the link's preview, accept and the creator's lists) are not personal state.
 */
export const AS_IF_PERSONAL_ROUTE_PATHS: readonly string[] = ['shared-items', 'invitations/mine'];

/**
 * The one personal route a view may read: the holder's own account (`GET auth/me`). The web boots with
 * the view's token and asks who is signed in; refused, it shows "Session could not be verified" and the
 * person is locked out of the tab, with no banner and no Stop. It has no work-area parameter, so it is
 * not an {@link AS_IF_ALLOWED_ROUTES} entry. It answers the person who started the view, whose own
 * `auth/me` it is, never a named member: the credential is the holder's own, and nothing else under
 * `auth` is reachable. ASIF-13, ASIF-29.
 */
export const AS_IF_OWN_ACCOUNT_ROUTE = { method: 'GET', path: 'auth/me' } as const;

export const isAsIfOwnAccountRoute = (
  method: string | undefined,
  routePath: string | undefined,
): boolean =>
  !!method &&
  !!routePath &&
  method.toUpperCase() === AS_IF_OWN_ACCOUNT_ROUTE.method &&
  trimPath(routePath).replace(/^api\//, '') === AS_IF_OWN_ACCOUNT_ROUTE.path;

/**
 * The one list a view reads that names no work area: the shell's work-area list (`GET work-areas`), read
 * on every screen. Refused, every page of a view is blank. It is not an {@link AS_IF_ALLOWED_ROUTES}
 * entry, because no path parameter binds it; the service narrows it instead: under a view it answers the
 * view's own work area alone, with what the role can see of it, never the holder's others
 * (`ProjectsService.findAllForUser`, ASIF-3). ASIF-29.
 */
export const AS_IF_VIEW_NARROWED_LIST_ROUTE = { method: 'GET', path: 'work-areas' } as const;

export const isAsIfViewNarrowedListRoute = (
  method: string | undefined,
  routePath: string | undefined,
): boolean =>
  !!method &&
  !!routePath &&
  method.toUpperCase() === AS_IF_VIEW_NARROWED_LIST_ROUTE.method &&
  routePath.replace(/^\/+|\/+$/g, '').replace(/^api\//, '') === AS_IF_VIEW_NARROWED_LIST_ROUTE.path;

/**
 * The GraphQL reads that are the person's own: what is waiting for them, their ideas, their agents, their
 * organisations and their own next-up settings. A view has none of them (ASIF-13), so a tab that holds a
 * view does not send them: the block that asks says so, with no request and no retry. Named by the
 * resolver field; the web reads the root fields of a query against this list (`asIfEnding.ts`).
 */
export const AS_IF_PERSONAL_OPERATION_FIELDS: readonly string[] = [
  'tasksWaitingForMe',
  'decisionsWaitingForMe',
  // F-0713 (AC-SV18): the access requests listed beside them.
  'accessRequestsWaitingForMe',
  'myIdeas',
  'userAgents',
  'namespaceTree',
  'nextUpConfigs',
  'workAreaAgentAssignability',
];

export const isAsIfPersonalRoute = (path: string): boolean => {
  const trimmed = path.split('?')[0].replace(/^\/+|\/+$/g, '');
  return (
    AS_IF_PERSONAL_ROUTE_PREFIXES.includes(trimmed.split('/')[0]) ||
    AS_IF_PERSONAL_ROUTE_PATHS.includes(trimmed)
  );
};

/**
 * A GraphQL operation an as-if credential may run. A listed **query** reads; a listed **mutation**
 * (`kind: 'mutation'`) is a write the view makes as the role: it still passes through the narrowed
 * access seam (the role's rights decide) and leaves an `as_if.act` row first (ASIF-5b). Nothing else
 * that writes is listed except the view's own stop, and decisions are never listed (ASIF-6).
 *
 * The operation is bound to the view's work area one of two ways. `workAreaArgs` name the work area in
 * the arguments, by path (`workAreaId`, or `input.workAreaId` inside an input object): at least one
 * must be given and every one given must be the view's own. `workItemArg` names the argument that
 * carries a work item's id; the item's own work area is resolved by the caller and compared, never one
 * the caller says. `refusedArgs` are the arguments that would widen the question beyond that work area
 * (a list of work areas, all of them, a namespace): the operation is refused when any of them is set.
 * Subscriptions are never listed.
 */
export interface IAsIfAllowedOperation {
  field: string;
  /** Absent means a query. */
  kind?: 'query' | 'mutation';
  workAreaArgs: readonly string[];
  workItemArg?: string;
  refusedArgs?: readonly string[];
}

const WORK_AREA = ['workAreaId', 'projectId'] as const;

/** The reads the web's work-area pages run (request0710s). Each is asked of `WorkAreaAccess` for its work area. */
export const AS_IF_ALLOWED_OPERATIONS: readonly IAsIfAllowedOperation[] = [
  { field: 'genericView', workAreaArgs: WORK_AREA },
  { field: 'genericViewMetadata', workAreaArgs: WORK_AREA },
  { field: 'genericViewCheckpoint', workAreaArgs: WORK_AREA },
  { field: 'workItem', workAreaArgs: WORK_AREA },
  { field: 'workItems', workAreaArgs: WORK_AREA },
  { field: 'workItemsByIds', workAreaArgs: WORK_AREA },
  { field: 'workItemsPage', workAreaArgs: WORK_AREA },
  { field: 'itemTypes', workAreaArgs: WORK_AREA, refusedArgs: ['workAreaIds'] },
  { field: 'workflowSnapshot', workAreaArgs: WORK_AREA },
  { field: 'decision', workAreaArgs: WORK_AREA },
  { field: 'decisions', workAreaArgs: WORK_AREA },
  // I-2171: what the work area's Home and its screens read beside the items: the statuses, the agents that
  // wrote here, the releases, the sync and activity checkpoints and the waiting items. Each is asked of
  // `WorkAreaAccess` for its work area (`work_area.read`, `work_item.read` or `release.read`), so the role's
  // rights decide. `as-if-home-reads.spec.ts` holds each to the permission its resolver asks.
  { field: 'workAreaStatuses', workAreaArgs: WORK_AREA },
  { field: 'workAreaActivityAgents', workAreaArgs: WORK_AREA },
  { field: 'releasesForWorkArea', workAreaArgs: WORK_AREA },
  { field: 'workItemSyncCheckpoint', workAreaArgs: WORK_AREA },
  { field: 'workItemActivityCheckpoint', workAreaArgs: WORK_AREA },
  { field: 'workAreaWaitingItems', workAreaArgs: ['workAreaId'] },
  // The writes a walk-through needs (ASIF-3): file a request, change an item, move it, and the one
  // Manager-only write that shows a role without the right is refused. Each is held to the view's work
  // area and asked of the narrowed seam, so the role's rights decide.
  {
    field: 'createWorkItem',
    kind: 'mutation',
    workAreaArgs: ['input.workAreaId', 'input.projectId'],
  },
  { field: 'updateWorkItem', kind: 'mutation', workAreaArgs: [], workItemArg: 'id' },
  { field: 'moveWorkItem', kind: 'mutation', workAreaArgs: [], workItemArg: 'id' },
  { field: 'patchWorkAreaStatuses', kind: 'mutation', workAreaArgs: WORK_AREA },
];

export type AsIfDecisionType = { allowed: true } | { allowed: false; why: string };

const trimPath = (path: string): string => path.replace(/^\/+|\/+$/g, '');

/** The listed entry a REST request is for, or null. Anything not listed is not here. */
export function asIfRouteEntry(
  method: string | undefined,
  routePath: string | undefined,
): IAsIfAllowedRoute | null {
  if (!method || !routePath) return null;
  const path = trimPath(routePath).replace(/^api\//, '');
  return (
    AS_IF_ALLOWED_ROUTES.find(
      (route) => route.method === method.toUpperCase() && route.path === path,
    ) ?? null
  );
}

/**
 * Whether a REST request is on the list, and for the bound work area. Anything unknown is refused.
 * `itemWorkAreaId` is the work area of the work item the path names, resolved by the caller for an entry
 * with a `workItemParam`; absent or null, that entry is refused.
 */
export function asIfRouteDecision(input: {
  method: string | undefined;
  routePath: string | undefined;
  params: Record<string, unknown> | undefined;
  /** The request's query, for an entry that names its work area there (`workAreaQuery`). */
  query?: Record<string, unknown>;
  boundWorkAreaId: string | null;
  itemWorkAreaId?: string | null;
}): AsIfDecisionType {
  const { method, routePath, params, query, boundWorkAreaId, itemWorkAreaId } = input;
  if (!boundWorkAreaId || !method || !routePath) return { allowed: false, why: 'unbound' };
  if (isAsIfOwnAccountRoute(method, routePath)) return { allowed: true };
  if (isAsIfViewNarrowedListRoute(method, routePath)) return { allowed: true };
  const entry = asIfRouteEntry(method, routePath);
  if (!entry) return { allowed: false, why: 'route' };
  const named = asIfRouteWorkAreaOf(entry, { params, query, itemWorkAreaId });
  return named === boundWorkAreaId ? { allowed: true } : { allowed: false, why: 'other-work-area' };
}

/**
 * The work area a request to a listed route names, by the entry's one way of naming it: the item's own
 * work area (resolved by the caller), a path parameter, or a query parameter (I-2557). Null when the
 * request does not name it, which the decision refuses.
 */
export function asIfRouteWorkAreaOf(
  entry: IAsIfAllowedRoute,
  request: {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    itemWorkAreaId?: string | null;
  },
): unknown {
  const { params, query, itemWorkAreaId } = request;
  if (entry.workItemParam) {
    return params?.[entry.workItemParam] !== undefined ? (itemWorkAreaId ?? null) : null;
  }
  if (entry.workAreaParam) return params?.[entry.workAreaParam] ?? null;
  if (entry.workAreaQuery) return query?.[entry.workAreaQuery] ?? null;
  return null;
}

const set = (value: unknown): boolean =>
  value !== undefined &&
  value !== null &&
  value !== false &&
  !(Array.isArray(value) && value.length === 0);

const argAt = (args: Record<string, unknown> | undefined, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined,
      args,
    );

/** The listed entry a GraphQL operation is for, or null. */
export const asIfOperationEntry = (field: string | undefined): IAsIfAllowedOperation | null =>
  (field && AS_IF_ALLOWED_OPERATIONS.find((operation) => operation.field === field)) || null;

/**
 * Whether a GraphQL operation is on the list, and for the bound work area only. `itemWorkAreaId` is the
 * work area of the work item the operation's `workItemArg` names, resolved by the caller; absent or null,
 * an entry with a `workItemArg` is refused.
 */
export function asIfOperationDecision(input: {
  field: string | undefined;
  args: Record<string, unknown> | undefined;
  boundWorkAreaId: string | null;
  itemWorkAreaId?: string | null;
}): AsIfDecisionType {
  const { field, args, boundWorkAreaId, itemWorkAreaId } = input;
  if (!boundWorkAreaId || !field) return { allowed: false, why: 'unbound' };
  const entry = asIfOperationEntry(field);
  if (!entry) return { allowed: false, why: 'operation' };
  if ((entry.refusedArgs ?? []).some((name) => set(argAt(args, name))))
    return { allowed: false, why: 'widened' };
  if (entry.workItemArg) {
    return set(argAt(args, entry.workItemArg)) && itemWorkAreaId === boundWorkAreaId
      ? { allowed: true }
      : { allowed: false, why: 'other-work-area' };
  }
  const named = entry.workAreaArgs.map((name) => argAt(args, name)).filter(set);
  return named.length > 0 && named.every((value) => value === boundWorkAreaId)
    ? { allowed: true }
    : { allowed: false, why: 'other-work-area' };
}

/** The sentence of the refusal: where the view cannot go, and what to do. */
export const AS_IF_NOT_HERE_MESSAGE =
  'This is not available while viewing as another role. Stop the view to do it as yourself. Nothing was changed.';
